import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Table,
} from "react-bootstrap";
import RedirectContainer from "../../../components/RedirectContainer";
import SidebarPanelNavigation from "../../../components/SidebarPanelNavigation";
import LoadingIndicator from "../../../components/LoadingIndicator";
import useNotification from "../../../contexts/notification";
import {
  createPromocao,
  getPromocoes,
  getLeads,
  updatePromocao,
  deletePromocao,
} from "../../../services/api";

function PromocaoModal({ show, onHide, saving, onSubmit, initialData, leads }) {
  const [formValues, setFormValues] = useState({ nome: "", link: "", leads: [] });

  useEffect(() => {
    if (initialData) {
      setFormValues({
        nome: initialData.nome || "",
        link: initialData.link || "",
        leads: (initialData.leads || []).map((lead) => lead.id),
      });
    } else {
      setFormValues({ nome: "", link: "", leads: [] });
    }
  }, [initialData, show]);

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit(formValues);
  };

  const handleMultiSelectChange = (event) => {
    const values = Array.from(event.target.selectedOptions).map((option) =>
      Number(option.value)
    );
    setFormValues((prev) => ({ ...prev, leads: values }));
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>
            {initialData ? "Editar promoção" : "Nova promoção"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={6} className="mb-3">
              <Form.Label>Nome *</Form.Label>
              <Form.Control
                value={formValues.nome}
                onChange={(event) =>
                  setFormValues((prev) => ({ ...prev, nome: event.target.value }))
                }
                placeholder="Ex.: Lançamento imperdível"
                required
              />
            </Col>
            <Col md={6} className="mb-3">
              <Form.Label>Link *</Form.Label>
              <Form.Control
                value={formValues.link}
                onChange={(event) =>
                  setFormValues((prev) => ({ ...prev, link: event.target.value }))
                }
                placeholder="https://..."
                required
              />
            </Col>
            <Col xs={12}>
              <Form.Label>Leads vinculados</Form.Label>
              <Form.Control
                as="select"
                multiple
                value={formValues.leads}
                onChange={handleMultiSelectChange}
              >
                {leads.map((lead) => (
                  <option key={lead.id} value={lead.id}>
                    {lead.name} - {lead.phone}
                  </option>
                ))}
              </Form.Control>
              <Form.Text className="text-muted">
                Os leads selecionados receberão esta oferta assim que entrarem na
                janela de reengajamento do WhatsApp.
              </Form.Text>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
            Cancelar
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? "Salvando..." : "Salvar"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default function PromotionsAdmin() {
  const [loading, setLoading] = useState(true);
  const [savingPromotion, setSavingPromotion] = useState(false);
  const [promotions, setPromotions] = useState([]);
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);
  const { setMessage } = useNotification();

  const loadData = useCallback(() => {
    setLoading(true);
    Promise.all([getPromocoes(), getLeads()])
      .then(([promotionsResponse, leadsResponse]) => {
        setPromotions(promotionsResponse);
        setLeads(leadsResponse);
      })
      .catch(() =>
        setMessage({
          error: true,
          content: "Não foi possível carregar as promoções. Tente novamente.",
        })
      )
      .finally(() => setLoading(false));
  }, [setMessage]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredPromotions = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) {
      return promotions;
    }

    return promotions.filter((promotion) =>
      [promotion.nome, promotion.link]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(normalizedSearch))
    );
  }, [promotions, search]);

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPromotion(null);
  };

  const handleSubmitPromotion = async (values) => {
    setSavingPromotion(true);
    try {
      const payload = { ...values };
      if (editingPromotion) {
        await updatePromocao(editingPromotion.id, payload);
        setMessage({
          error: false,
          content: "Promoção atualizada com sucesso.",
        });
      } else {
        await createPromocao(payload);
        setMessage({
          error: false,
          content: "Promoção criada com sucesso.",
        });
      }

      handleCloseModal();
      loadData();
    } catch (error) {
      setMessage({
        error: true,
        content: "Não foi possível salvar a promoção. Verifique os dados.",
      });
    } finally {
      setSavingPromotion(false);
    }
  };

  const handleDeletePromotion = async (id) => {
    if (!window.confirm("Deseja realmente excluir esta promoção?")) {
      return;
    }

    try {
      await deletePromocao(id);
      setMessage({ error: false, content: "Promoção removida." });
      loadData();
    } catch (error) {
      setMessage({
        error: true,
        content: "Não foi possível remover a promoção.",
      });
    }
  };

  const totalPromotions = promotions.length;
  const totalLinkedLeads = promotions.reduce(
    (accumulator, promotion) => accumulator + (promotion.leads?.length || 0),
    0
  );

  const averageLeadsPerPromotion = totalPromotions
    ? Math.round((totalLinkedLeads / totalPromotions) * 10) / 10
    : 0;

  return (
    <RedirectContainer role="admin">
      <Container fluid>
        <div>
          <SidebarPanelNavigation isAdmin />
          <div className="content-panel">
            {loading ? (
              <LoadingIndicator />
            ) : (
              <>
                <Row className="align-items-center">
                  <Col md={8} className="mb-3">
                    <h2 className="mb-1">Promoções e Ofertas</h2>
                    <p className="text-muted mb-0">
                      Organize os conteúdos promocionais e distribua para os leads certos no momento ideal.
                    </p>
                  </Col>
                  <Col md={4} className="text-md-right text-sm-left mb-3">
                    <Button onClick={() => setShowModal(true)}>Nova promoção</Button>
                  </Col>
                </Row>

                <Row className="my-3">
                  <Col xl={4} md={6} className="mb-3">
                    <Card>
                      <Card.Body>
                        <h3 className="text-primary mb-1">{totalPromotions}</h3>
                        <span className="text-muted">Promoções ativas</span>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col xl={4} md={6} className="mb-3">
                    <Card>
                      <Card.Body>
                        <h3 className="text-success mb-1">{totalLinkedLeads}</h3>
                        <span className="text-muted">Leads vinculados</span>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col xl={4} md={6} className="mb-3">
                    <Card>
                      <Card.Body>
                        <h3 className="text-info mb-1">{averageLeadsPerPromotion}</h3>
                        <span className="text-muted">Leads por promoção</span>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                <Card className="mb-3">
                  <Card.Body>
                    <Row>
                      <Col md={6} className="mb-3">
                        <Form.Label>Buscar</Form.Label>
                        <Form.Control
                          placeholder="Nome ou link"
                          value={search}
                          onChange={(event) => setSearch(event.target.value)}
                        />
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                <Card>
                  <Card.Body className="table-responsive">
                    <Table hover responsive>
                      <thead>
                        <tr>
                          <th>Promoção</th>
                          <th>Link</th>
                          <th>Leads</th>
                          <th className="text-right">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredPromotions.length === 0 && (
                          <tr>
                            <td colSpan={4} className="text-center text-muted py-4">
                              Nenhuma promoção encontrada.
                            </td>
                          </tr>
                        )}
                        {filteredPromotions.map((promotion) => (
                          <tr key={promotion.id}>
                            <td className="align-middle">
                              <strong>{promotion.nome}</strong>
                            </td>
                            <td className="align-middle" style={{ minWidth: 220 }}>
                              <a
                                href={promotion.link}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {promotion.link}
                              </a>
                            </td>
                            <td className="align-middle" style={{ minWidth: 200 }}>
                              {(promotion.leads || []).length > 0 ? (
                                promotion.leads.map((lead) => (
                                  <Badge key={lead.id} variant="info" className="mr-1 mb-1">
                                    {lead.name}
                                  </Badge>
                                ))
                              ) : (
                                <span className="text-muted">Sem leads vinculados</span>
                              )}
                            </td>
                            <td className="align-middle text-right" style={{ minWidth: 180 }}>
                              <Button
                                variant="link"
                                className="p-0 mr-3"
                                onClick={() => {
                                  setEditingPromotion(promotion);
                                  setShowModal(true);
                                }}
                              >
                                Editar
                              </Button>
                              <Button
                                variant="link"
                                className="text-danger p-0"
                                onClick={() => handleDeletePromotion(promotion.id)}
                              >
                                Remover
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>

                <PromocaoModal
                  show={showModal}
                  onHide={handleCloseModal}
                  onSubmit={handleSubmitPromotion}
                  saving={savingPromotion}
                  initialData={editingPromotion}
                  leads={leads}
                />
              </>
            )}
          </div>
        </div>
      </Container>
    </RedirectContainer>
  );
}

